import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

/**
 * <gift-with-purchase-usp>
 *
 * Product-page line advertising the gift-with-purchase offer. Because it lives
 * on the product page (NOT inside the cart section that morphs on updates), it
 * listens for cart updates and recomputes the remaining spend client-side
 * against the GWP threshold (passed in via data attributes).
 *
 * Three states, driven by the current cart total:
 *   - empty cart  → the "initial" advert, using the full threshold amount
 *   - part-way    → "progress" copy, using the amount still needed
 *   - threshold met → "unlocked" copy
 *
 * The initial state is rendered server-side in Liquid (from the live `cart`
 * object) so there's no flash; this element keeps it in sync afterwards and
 * re-syncs once on connect in case the page HTML was cached.
 *
 * Data attributes:
 *   data-threshold          Threshold in minor units (cents).
 *   data-initial-template   Copy for an empty cart. `[amount]` = full threshold.
 *   data-progress-template  Copy while below threshold. `[amount]` = remaining.
 *   data-unlocked-template  Copy once the threshold is reached.
 *   data-product            Gift product title (rendered bold in place of `[product]`).
 *   data-money-format       Shopify money format template (e.g. "£{{amount}}").
 *   data-currency           ISO currency code (e.g. "GBP").
 */
class GiftWithPurchaseUsp extends HTMLElement {
  /** @type {HTMLElement | null} */
  #textEl = null;
  /** @type {AbortController | null} */
  #inFlight = null;

  connectedCallback() {
    this.#textEl = this.querySelector('[data-gwp-text]');
    this.threshold = parseInt(this.dataset.threshold ?? '0', 10);
    this.initialTemplate = this.dataset.initialTemplate ?? 'Spend [amount], get a free [product]';
    this.progressTemplate = this.dataset.progressTemplate ?? 'Spend [amount] more for a free [product]';
    this.unlockedTemplate = this.dataset.unlockedTemplate ?? "You've unlocked a free [product]!";
    this.productTitle = this.dataset.product ?? '';
    this.moneyFormat = this.dataset.moneyFormat ?? '{{amount}}';
    this.currency = this.dataset.currency || 'USD';

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // Correct the server-rendered state in case the page was served from cache.
    this.#syncFromCart();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#inFlight?.abort();
  }

  #onCartUpdate = async (event) => {
    // Prefer the total carried on the event; fall back to the authoritative
    // /cart.js when the source doesn't include it (e.g. product-form adds).
    let totalCents = event?.detail?.resource?.total_price;

    if (typeof totalCents !== 'number') {
      totalCents = await this.#fetchCartTotal();
    }

    if (typeof totalCents === 'number') this.#render(totalCents);
  };

  async #syncFromCart() {
    const totalCents = await this.#fetchCartTotal();
    if (typeof totalCents === 'number') this.#render(totalCents);
  }

  /** @returns {Promise<number | null>} */
  async #fetchCartTotal() {
    this.#inFlight?.abort();
    this.#inFlight = new AbortController();

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#inFlight.signal,
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return typeof cart.total_price === 'number' ? cart.total_price : null;
    } catch (_) {
      return null;
    }
  }

  /** @param {number} totalCents */
  #render(totalCents) {
    if (!this.#textEl) return;

    const remaining = this.threshold - totalCents;

    if (totalCents <= 0) {
      // Empty cart: advertise the full offer.
      this.removeAttribute('data-complete');
      this.#write(this.initialTemplate, formatMoney(this.threshold, this.moneyFormat, this.currency));
    } else if (remaining > 0) {
      // Part-way there: show how much more is needed.
      this.removeAttribute('data-complete');
      this.#write(this.progressTemplate, formatMoney(remaining, this.moneyFormat, this.currency));
    } else {
      // Threshold met.
      this.setAttribute('data-complete', '');
      this.#write(this.unlockedTemplate, '');
    }
  }

  /**
   * Render a template into the text element, substituting `[amount]` (plain
   * text) and `[product]` (the gift title, bold). Built with DOM nodes rather
   * than innerHTML so the product title is never treated as markup.
   * @param {string} template
   * @param {string} amount
   */
  #write(template, amount) {
    if (!this.#textEl) return;

    const withAmount = template.replaceAll('[amount]', amount);
    const parts = withAmount.split('[product]');

    this.#textEl.textContent = '';
    parts.forEach((part, index) => {
      if (part) this.#textEl.appendChild(document.createTextNode(part));
      if (index < parts.length - 1) {
        const strong = document.createElement('strong');
        strong.className = 'usp__gift-name';
        strong.textContent = this.productTitle;
        this.#textEl.appendChild(strong);
      }
    });
  }
}

if (!customElements.get('gift-with-purchase-usp')) {
  customElements.define('gift-with-purchase-usp', GiftWithPurchaseUsp);
}
